/**
 * Web Push Notification Service
 * Handles browser push notifications using Web Push API
 */
import createVisibilityPoller from './VisibilityPoller';

class NotificationService {
  constructor() {
    this.registration = null;
    this.subscription = null;
    this.onNotificationClick = null;
    this.checkInterval = null;
  }

  /**
   * Request notification permission
   */
  async requestPermission() {
    if (!('Notification' in window)) {
      console.warn('This browser does not support notifications');
      return false;
    }

    if (Notification.permission === 'granted') {
      return true;
    }

    if (Notification.permission === 'denied') {
      console.warn('Notification permission denied');
      return false;
    }

    // Request permission - browser will show native popup
    const permission = await Notification.requestPermission();
    return permission === 'granted';
  }

  /**
   * Get current notification permission status
   */
  getPermissionStatus() {
    if (!('Notification' in window)) {
      return 'not-supported';
    }
    return Notification.permission; // 'default', 'granted', or 'denied'
  }

  /**
   * Check if push notifications are enabled
   */
  async isPushEnabled() {
    try {
      if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
        return false;
      }

      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      return subscription !== null;
    } catch (error) {
      console.error('Error checking push status:', error);
      return false;
    }
  }

  /**
   * Initialize service worker and get subscription
   */
  async initialize() {
    if (!('serviceWorker' in navigator)) {
      console.warn('Service Worker not supported');
      return false;
    }

    try {
      // Register service worker
      this.registration = await navigator.serviceWorker.register('/service-worker.js');
      console.log('Service Worker registered:', this.registration);

      // Wait for service worker to be ready
      await navigator.serviceWorker.ready;

      // Get existing subscription or create new one
      this.subscription = await this.registration.pushManager.getSubscription();
      
      if (!this.subscription) {
        // Create new subscription
        const hasPermission = await this.requestPermission();
        if (!hasPermission) {
          console.warn('Notification permission not granted');
          return false;
        }

        // Generate VAPID public key from backend
        const vapidPublicKey = await this.getVapidPublicKey();
        if (!vapidPublicKey) {
          console.error('Failed to get VAPID public key');
          return false;
        }

        this.subscription = await this.registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: this.urlBase64ToUint8Array(vapidPublicKey)
        });
      }

      // Send subscription to backend
      await this.sendSubscriptionToBackend(this.subscription);

      // Listen for push events
      navigator.serviceWorker.addEventListener('message', (event) => {
        if (event.data && event.data.type === 'NOTIFICATION_CLICK') {
          if (this.onNotificationClick) {
            this.onNotificationClick(event.data.payload);
          }
        }
      });

      console.log('Push notification initialized successfully');
      return true;
    } catch (error) {
      console.error('Error initializing push notifications:', error);
      return false;
    }
  }

  /**
   * Get VAPID public key from backend
   */
  async getVapidPublicKey() {
    try {
      const { apiCall } = require('../helpers/MainHelper');
      const response = await apiCall('business/notifications/push/vapid-key', 'get', null);
      return response?.publicKey || null;
    } catch (error) {
      console.error('Error getting VAPID key:', error);
      return null;
    }
  }

  /**
   * Send subscription to backend
   */
  async sendSubscriptionToBackend(subscription) {
    try {
      const { apiCall } = require('../helpers/MainHelper');
      const subscriptionData = {
        endpoint: subscription.endpoint,
        keys: {
          p256dh: this.arrayBufferToBase64(subscription.getKey('p256dh')),
          auth: this.arrayBufferToBase64(subscription.getKey('auth'))
        }
      };

      await apiCall('business/notifications/push/subscribe', 'post', subscriptionData);
      console.log('Subscription sent to backend');
    } catch (error) {
      console.error('Error sending subscription to backend:', error);
    }
  }

  /**
   * Unsubscribe from push notifications
   */
  async unsubscribe() {
    try {
      if (this.subscription) {
        await this.subscription.unsubscribe();
        const { apiCall } = require('../helpers/MainHelper');
        await apiCall('business/notifications/push/unsubscribe', 'post', {
          endpoint: this.subscription.endpoint
        });
        this.subscription = null;
        console.log('Unsubscribed from push notifications');
      }
    } catch (error) {
      console.error('Error unsubscribing:', error);
    }
  }

  /**
   * Set notification click handler
   */
  setNotificationClickHandler(handler) {
    this.onNotificationClick = handler;
  }

  /**
   * Show local notification (for testing)
   */
  async showLocalNotification(title, options = {}) {
    const hasPermission = await this.requestPermission();
    if (!hasPermission) {
      return false;
    }

    const notification = new Notification(title, {
      icon: '/logo-black.png',
      badge: '/logo-black.png',
      ...options
    });

    notification.onclick = () => {
      if (this.onNotificationClick && options.data) {
        this.onNotificationClick(options.data);
      }
      notification.close();
    };

    return true;
  }

  /**
   * Start polling for new notifications (fallback)
   */
  startPolling(callback, interval = 30000) {
    this.stopPolling();
    this.checkInterval = createVisibilityPoller(callback, interval, { immediate: true });
  }

  /**
   * Stop polling
   */
  stopPolling() {
    if (this.checkInterval) {
      this.checkInterval();
      this.checkInterval = null;
    }
  }

  /**
   * Convert VAPID key to Uint8Array
   */
  urlBase64ToUint8Array(base64String) {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding)
      .replace(/\-/g, '+')
      .replace(/_/g, '/');

    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);

    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  }

  /**
   * Convert ArrayBuffer to Base64
   */
  arrayBufferToBase64(buffer) {
    const bytes = new Uint8Array(buffer);
    let binary = '';
    for (let i = 0; i < bytes.byteLength; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return window.btoa(binary);
  }
}

export default new NotificationService();
